import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Status, USER_STATUS_TYPES_TO_CLASSNAME } from 'components/Status'
import { Link } from 'components/Link'
import { Dropdown } from 'components/Dropdown'
import { StartChat } from '../../../../../components/StartChat/StartChat'
import { Avatar } from '../../../../../components/Avatar/Avatar'
import { DescriptionCell } from '../../../../../components/DescriptionCell/DescriptionCell'
import { CellWithSubtitle } from 'components/CellWithSubtitle/CellWithSubtitle'
import { Sorter } from 'components/Sorter/Sorter'
import { fullFormatPhoneNumber } from 'helper/common'
import { getDepartmentsList, getHospitalsList } from '../../../api'
import { ACTIONS, ability, SUBJECTS, ROLES } from 'features/Permission'
import { ROLES_NAMES } from '../../../../../constants'
import {
  MY_COMMUNITY_KEYS,
  MY_COMMUNITY_USER_STATUSES
} from '../../../constants'
import { routes } from 'router'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import {
  HealthSystemUser,
  HealthSystemUsersColumnsFunc,
  HealthSystemUsersFilters,
  HealthSystemUsersFiltersFunc
} from './types'
import { getContractCategoriesListByRole } from './api'

export const USER_STATUSES_TABS = [
  {
    label: 'All',
    value: ''
  },
  {
    label: 'Pending',
    value: 'Pending'
  }
]

export const columns = ({
  applyFilters,
  filters,
  hsId,
  user,
  selectedRole,
  onStartChat,
  canEditUser
}: HealthSystemUsersColumnsFunc): ColumnsType<HealthSystemUser> => {
  const isSameHSOrCommunityPresident =
    user.role !== ROLES.COMMUNITY_PRESIDENT ? hsId === user.health_system : true
  return [
    {
      dataIndex: 'role',
      key: 'role',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      render: (text) => ROLES_NAMES[text] ?? <>–</>,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'first_name',
      key: 'first_name',
      className: 'avatar-cell',
      width: 44,
      title: null,
      render: (_, { first_name, avatar_logo, last_name }) => (
        <Avatar
          name={`${first_name[0]?.toUpperCase()}${last_name[0]?.toUpperCase()}`}
          src={avatar_logo?.file_preview || ''}
          alt="user image"
        />
      ),
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'name',
      key: 'name',
      width: 220,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          User name
        </Sorter>
      ),
      render: (_, { first_name, last_name, uuid }) =>
        (ability.can(
          ACTIONS.VIEW,
          SUBJECTS.HEALTH_SYSTEM_USERS,
          selectedRole ?? undefined
        ) ||
          canEditUser) &&
        isSameHSOrCommunityPresident ? (
          <CellWithSubtitle
            title={
              <Link
                to={`${routes.createUserProfile}/${uuid}?fromCommunity=true`}
                state={{
                  redirectTab: MY_COMMUNITY_KEYS.communities,
                  selectedHs: hsId
                }}
              >
                {first_name} {last_name}
              </Link>
            }
          />
        ) : (
          `${first_name} ${last_name}`
        ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 130,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          items={MY_COMMUNITY_USER_STATUSES}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status, uuid }) => (
        <div className="row gap-8 align-center">
          <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
            {status}
          </Status.Default>
          {status === 'Active' && (
            <StartChat onClick={() => onStartChat(uuid)} />
          )}
        </div>
      ),
      filterIcon: !!filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: 240,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="email"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Email
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      title: 'Phone #',
      width: 196,
      render: (_, { phone_number }) =>
        phone_number ? fullFormatPhoneNumber(phone_number) : '-',
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'hospitals',
      key: 'hospitals',
      title: 'Hospital',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getHospitalsList(hsId)}
          field="hospitals"
          asyncSearch
          {...props}
        />
      ),
      filterIcon: !!filters?.hospitals?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'departments',
      key: 'departments',
      title: 'Departments',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getDepartmentsList(hsId)}
          field="departments"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { departments }) => {
        return <DescriptionCell content={departments} text={departments} />
      },
      filterIcon: filters?.departments?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'contract_categories',
      key: 'contract_categories',
      width: 240,
      title: 'Contract categories',
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() =>
            getContractCategoriesListByRole(hsId, selectedRole)
          }
          field="contract_categories"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories }) => {
        return (
          <DescriptionCell
            content={contract_categories}
            text={contract_categories}
          />
        )
      },
      filterIcon: !!filters?.contract_categories?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    }
  ]
}
